/**
 * **`compileRuling`** — turn a GM's ad-hoc {@link RulingSpec} into ordinary {@link EffectEntry}
 * clauses, so a ruling minted mid-encounter runs through exactly the same engine every authored
 * status runs through.
 *
 * This is the load-bearing half of the atoms-not-scripting stance. The palette is a **form over
 * the existing atom vocabulary**, never an expression field: `RulingSpec` is a
 * closed record of primitives (declared in `@lancer-vtt/domain`, which cannot see this package's
 * effect AST at all), and this function is the only thing that turns it into effects. A GM
 * therefore *structurally* cannot express anything enumerated below — the constraint is the
 * type, not a convention someone can erode.
 *
 * **This is not a second source of truth for status mechanics.** It compiles *into* the one
 * vocabulary rather than running beside it, and `rulingCompile.test.ts` parses every entry it
 * emits through the shipped authoring gate (`EffectEntrySchema`), so the compiler cannot drift
 * from the DSL without a red test. That pin is not optional garnish: the authoring guardrails —
 * `lint.ts`, `coverage.ts`, `inertTriggers.ts`, `mechanization.ts` — all walk the *authored
 * corpus*, so a compiled ruling is never linted or coverage-counted. The schema pin is the only
 * gate this output ever passes.
 *
 * **What this compiler deliberately does NOT emit, and why it is a hard gate rather than a
 * silent omission — see {@link unreachableRulingFacets}.**
 */
import type { RulingSpec } from "@lancer-vtt/domain";
import type { EffectEntry, ModifierContribution } from "./atoms";

/**
 * The facets of a {@link RulingSpec} this compiler cannot yet turn into a clause that actually
 * FIRES, each with the engine reason. Returns an empty array for a spec it can compile whole.
 *
 * The referee-tier gate calls this and rejects the command outright, rather than compiling a
 * clause that schema-validates and then never fires. That choice is the point: a GM who grants
 * "+1d6 on your next attack" and watches it do nothing has been lied to by the UI about the
 * rules, which is worse than the button being unavailable. A loud rejection names the gap; a
 * silent no-op hides it until someone notices at the table, mid-encounter, with players
 * watching.
 *
 * An entry belongs here only while the engine seam it needs is missing; building the seam is
 * what removes it.
 */
/**
 * The maximum damage an `extraDamage` spec could possibly add. Used only to answer "does this
 * grant anything at all" — a spec that answers zero compiles to an `extra_damage` atom that
 * fires and adds nothing, which is the silent-no-op shape this whole gate exists to refuse.
 *
 * Zero is reachable more ways than an absent field: `{}`, `{ amount: 0 }`, and `{ dice: { count:
 * 0, sides: 6 } }` all grant nothing, and so does `0d0`, which is what a CLI user typing
 * `--damage-dice 0d0` produces. Checking presence rather than magnitude would let every one of
 * those through.
 */
function grantedDamage(d: NonNullable<RulingSpec["extraDamage"]>): number {
  const dice = d.dice === undefined ? 0 : Math.max(0, d.dice.count) * Math.max(0, d.dice.sides);
  return dice + Math.max(0, d.amount ?? 0);
}

export function unreachableRulingFacets(spec: RulingSpec): string[] {
  const out: string[] = [];
  if (spec.stacks !== undefined && (!Number.isInteger(spec.stacks) || spec.stacks < 1)) {
    out.push("stacks: must be a whole number of at least 1");
  }
  if (spec.extraDamage !== undefined && grantedDamage(spec.extraDamage) === 0) {
    out.push("extraDamage: needs at least one die or a positive amount — this grants nothing");
  }
  return out;
}

/**
 * Compile a ruling into the clauses the engine gathers off its carrying status.
 *
 * Returns `[]` for an advisory-only ruling (a `reason` and nothing else) — the honest-degrade
 * path, and a real intended outcome rather than a failure: the status still renders as a chip
 * carrying the GM's stated reason, which beats a verbal ruling everyone forgets by round 3.
 *
 * Assumes the spec has already passed {@link unreachableRulingFacets} at the referee tier. It
 * is a pure function over a validated spec, so it does not re-gate; a caller that skips the
 * gate gets clauses that schema-validate and silently never fire.
 *
 * @param name the carrying status's name — the self-removal clause has to name the status it
 *   burns, and a status's mechanics are addressed by name everywhere else in the engine too.
 */
export function compileRuling(spec: RulingSpec, name: string): EffectEntry[] {
  const out: EffectEntry[] = [];
  // A stacked ruling ("Rattled 2") is its spec that many times over: the accuracy and the bonus
  // damage scale, and the one burn below still removes the whole stack.
  const stacks = spec.stacks ?? 1;

  // ── the continuous grant ───────────────────────────────────────────────────────
  // `attackerAccuracy` folds attacker-side, and `foldModifiers` sums it additively; `dice.ts`
  // then cancels accuracy against difficulty 1:1 and rolls |n| d6 keeping the single highest.
  // RAW-correct stacking falls out of the existing fold — nothing here implements it, which is
  // the entire argument for compiling into the vocabulary instead of beside it.
  //
  // A zero is dropped rather than emitted: an `attackerAccuracy: 0` modifier would show up in
  // `accuracyContributions` as a named source contributing nothing, which reads to a player as
  // a bug in the ruling rather than as a ruling that grants no accuracy.
  if (spec.accuracy !== undefined && spec.accuracy !== 0) {
    const mod: ModifierContribution = {
      attackerAccuracy: spec.accuracy * stacks,
      ...(spec.verb !== undefined ? { attackScope: { verb: spec.verb } } : {}),
    };
    out.push({ kind: "modifier", mod });
  }

  // ── bonus damage ───────────────────────────────────────────────────────────────
  // Damage is an ATOM (`extra_damage`), never a `ModifierContribution` facet — which is also
  // exactly why an inline-`mod` carrier could not express this half of the palette, and part of
  // the argument for compiling a spec into the vocabulary instead.
  //
  // It rides a RESOLVED-phase attack clause so the dice are drawn once the hit is known: `hit`
  // is `undefined` at the declared phase, so the strict-equality match against `true` fails
  // there and this fires exactly once, on a hit. A miss adds no damage, which is what a GM
  // means by "hit them for an extra d6" — there is nothing to add damage to on a miss.
  if (spec.extraDamage !== undefined && grantedDamage(spec.extraDamage) > 0) {
    out.push({
      kind: "triggered",
      on: "attack",
      match: {
        family: "attack",
        hit: true,
        ...(spec.verb !== undefined ? { verb: spec.verb } : {}),
      },
      effect: [
        {
          op: "extra_damage",
          // RAW's BONUS DAMAGE (Core p.67 sidebar; restated in COMBAT TERMINOLOGY, p.78). A GM
          // typing "+1d6 damage on the hit" into a ruling means exactly the printed term, so the
          // atom says so — and an area attack therefore halves it, like any other rider.
          bonusDamage: true,
          ...(spec.extraDamage.dice !== undefined ? { dice: { ...spec.extraDamage.dice, count: spec.extraDamage.dice.count * stacks } } : {}),
          ...(spec.extraDamage.amount !== undefined ? { amount: spec.extraDamage.amount * stacks } : {}),
        },
      ],
    });
  }

  // ── "your next attack" ─────────────────────────────────────────────────────────
  // A self-removal on the carrier's own status, fired at the DECLARED phase — before the to-hit
  // roll — so the ruling burns on the attempt, hit or miss. That is what a GM means by "your
  // next attack." The bonus still lands on that same attack: `resolve()` is pure, so the
  // modifier fold reads unmutated state while the removal commits as a recorded step, and
  // declared steps survive a miss unconditionally.
  //
  // Two behaviours worth knowing rather than discovering in play: a REJECTED or needs-decision
  // attempt discards its declared steps entirely, so a ruling is not burned by an attack that
  // never happened (correct, and intended); and a REACTION attack routes through `resolve()`
  // too, so it does consume the ruling (accepted as correct — the palette's tooltip says so).
  //
  // `match` is MANDATORY here, not decorative. `TriggerFamily` is a pipeline-*moment* family;
  // the phase lives in `AttackMatch.phase`, which is the double-fire-avoidance mechanism. A
  // match-less clause avoids double-firing only by an accident of gather asymmetry (the
  // hit/crit block gathers no status handlers; the miss block gathers the TARGET's), and
  // relying on that accident is how a later gather change silently burns the ruling twice.
  if (spec.consumeOnAttack === true) {
    out.push({
      kind: "triggered",
      on: "attack",
      match: {
        family: "attack",
        phase: "declared",
        // Scope the BURN to the same verb the accuracy is scoped to, so "+1 to your next Ram"
        // is not spent by an unrelated Skirmish.
        ...(spec.verb !== undefined ? { verb: spec.verb } : {}),
      },
      effect: [{ op: "remove_status", status: name, target: "self" }],
    });
  }

  // ── the advisory fallback ──────────────────────────────────────────────────────
  // A ruling with no mechanical facet is the HONEST-DEGRADE path, not a failure: the GM has
  // ruled something the vocabulary cannot express, and a visible chip carrying their stated
  // reason still beats a verbal ruling everyone forgets by round 3. It is also where the one
  // genuinely unbuildable preset lands — a damage PENALTY has no primitive at all
  // (`extra_damage` has no negative form; `reduce_pending_damage` is reaction-window only).
  //
  // Rather than compile to nothing, emit the `advisory` facet so the reason ALSO surfaces in the
  // resolution warnings when the carrier attacks — the ruling shows up in the log at the moment
  // it is relevant, instead of only on a chip someone has to hover.
  //
  // **Its one honest limit, stated because it is invisible from the outside:** `foldModifiers`
  // pushes advisories only from the ATTACKER-side fold, so an advisory ruling on a *defender*
  // never surfaces a warning. It is still a visible chip carrying the reason — the degrade is
  // real, just quieter on that side. Do not "fix" this by folding advisories target-side; that
  // would put a defender's own notes into the attacker's warning list.
  if (out.length === 0) {
    out.push({ kind: "modifier", mod: { advisory: `GM ruling — ${name}: ${spec.reason}` } });
  }

  return out;
}
